import { vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import { passVec2 } from "../box2dUtils";
import { Consumable, ConsumableType, Consumer, OnConsume, Physics, Stunned, Transform } from "../components";
import { PhysicsConstants, ResourceConstants } from "../constants";
import { destroyEntity, } from "../entityFactory";
import { Game } from "../game";
import { triggerEffect } from "../triggeredEffects";
import { addComponent } from "../utils";

export class ConsumeSystem extends System {
	private game: Game;
	private consumerMap = new Map<Entity, [Entity, number]>();

	constructor(world: any, attributes: any) {
		super(world, attributes);
		this.game = attributes.game;
	}

	init() { }

	execute(delta: number) {
		this.consumerMap.clear();

		if (!this.game.inSimulation) return;

		for (const entity of this.queries.onConsume.results) {
			const onConsume = entity.getComponent(OnConsume);
			onConsume.currentCooldown = Math.max(0, onConsume.currentCooldown - PhysicsConstants.StepRate);
		}

		//Consumers catch consumables and start consuming them
		for (const entity of this.queries.consumer.results) {
			const consumer = entity.getComponent(Consumer);
			const consumerTransform = entity.getComponent(Transform);
			for (const consumeTarget of consumer.targets) {

				const consumable = consumeTarget.getComponent(Consumable);
				if (!consumable || !consumeTarget.hasComponent(Transform)) {
					console.warn('found invalid consumable in targets');
					continue
				};

				const consumeTargetConsumer = this.consumerMap.get(consumeTarget);
				const targetTransform = consumeTarget.getComponent(Transform);
				const sqrDistance = vec2.squaredDistance(consumerTransform.position, targetTransform.position)
				if (!consumeTargetConsumer) {
					this.consumerMap.set(consumeTarget, [entity, sqrDistance]);
				}
				else {
					const competitorSqrDistance = consumeTargetConsumer[1];
					if (sqrDistance < competitorSqrDistance) {
						this.consumerMap.set(consumeTarget, [entity, sqrDistance]);
					}
				}

				//Disable physics things we consume. Not needed currently, was only relevent for bullets and tags, but they have different mechanisms now
				// if (consumeTarget.hasComponent(Physics)) {
				// 	addComponent(consumeTarget, Stunned, { duration: Infinity });
				// }
			}
		}

		//Things getting consumed move towards consumers and cause a trigger on overlap
		const diff = new vec2;

		const consuming = this.consumerMap.entries();
		for (const entry of consuming) {
			const consumableEntity = entry[0];
			const consumerEntity = entry[1][0];

			const transform = consumableEntity.getComponent(Transform);
			const physics = consumableEntity.getComponent(Physics);
			const consumer = consumerEntity.getComponent(Consumer);
			const target = consumerEntity.getComponent(Transform);
			const consumable = consumableEntity.getComponent(Consumable);

			//Move towards consumer
			vec2.difference(target.position, transform.position, diff);
			const distance = diff.length();
			if (distance > target.size.x / 2) {

				//Bullets and tags have to be aimed to hit the consumer, they are not attracted
				if (consumable.type === ConsumableType.Bullet || consumable.type === ConsumableType.Interferon) {
					continue;
				}

				transform.position.add(diff.normalize().scale(ResourceConstants.ConsumeSpeed));
				if (physics) {
					const body = consumableEntity.getMutableComponent(Physics).body;
					body.SetLinearVelocity(passVec2(vec2.zero));
					body.SetTransform(passVec2(transform.position), body.GetAngle());
				}
			}
			else if (!consumable.consumed) {
				//Consume
				consumer.consumedAmounts[consumable.type] += 1;
				consumable.consumed = true;

				//Trigger effects
				const onConsume = consumerEntity.getComponent(OnConsume);
				if (onConsume) {
					if (onConsume.requiredAmount.every((entry) => consumer.consumedAmounts[entry.type] >= entry.amount)
						&& onConsume.currentCooldown === 0
					) {
						//Trigger effect and reduce consumed counters
						triggerEffect(onConsume.effect, consumerEntity, consumableEntity);
						onConsume.requiredAmount.forEach((entry) => {
							consumer.consumedAmounts[entry.type] -= entry.amount
						});
						onConsume.currentCooldown = onConsume.cooldown;
					}
				}

				//This is messy and will probably cause a problem at some point
				destroyEntity(consumableEntity);
			}
		}

	}
}

ConsumeSystem.queries = {
	consumer: { components: [Consumer, Transform] },
	onConsume: { components: [OnConsume] },
}